import React, { useState } from 'react';
import { Row, Col, Card, CardBody, Collapse } from 'reactstrap';
import { Form } from '@availity/form';
import { Input, Select, Switch, FieldArray } from '../../lib/form-field';
import formValidation from './formValidation';

import * as countriesActions from '../../store/countries/actions';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import companyPhoneExchanges from '../../config/companyPhoneExchanges';
import TIMEZONES from '../../config/timezones';
import LANGUAGE_TAGS from '../../config/languageTags';
import { SubmitButton } from '../Common/Modal/modalActionButtons';
import {
  WEEKDAYS,
  callCenterFormValues,
  callCenterFromForm,
} from './callCenterForm';

const FormCountry = (props) => {
  const editingCountry = props.country || {};

  const facebookAdAccountIds = editingCountry.facebookAdAccountIds || [];

  const [active, setActive] = useState(editingCountry.active || false);
  const [facebookModule, setFacebookModule] = useState(
    editingCountry.facebookModule || false,
  );
  const [googleModule, setGoogleModule] = useState(
    editingCountry.googleModule || false,
  );
  const [supplierModule, setSupplierModule] = useState(
    editingCountry.supplierModule || false,
  );
  const [audienceModule, setAudienceModule] = useState(
    editingCountry.audienceModule || false,
  );
  const [landingPageModule, setLandingPageModule] = useState(
    editingCountry.landingPageModule || false,
  );
  const [aiModule, setAiModule] = useState(editingCountry.aiModule || false);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleSubmit = (values) => {
    const countryData = {
      ...values,
      // null when no weekday is open: the countries saga removes the field,
      // see callCenterFromForm for why a closed week is not a call center.
      callCenter: callCenterFromForm(values.callCenter),
      ...(typeof values.zernioAccountId === 'string' && {
        zernioAccountId: values.zernioAccountId.trim(),
      }),
      active,
      facebookModule,
      googleModule,
      supplierModule,
      audienceModule,
      landingPageModule,
      aiModule,
    };

    dispatch(
      props.create
        ? countriesActions.createCountry(countryData)
        : countriesActions.updateCountry({
            id: editingCountry.id,
            ...countryData,
          }),
    );
    props.closeModal();
  };

  return (
    <Card>
      <CardBody>
        <Form
          initialValues={{
            ...editingCountry,
            facebookAdAccountIds,
            facebookModule,
            googleModule,
            supplierModule,
            audienceModule,
            landingPageModule,
            aiModule,
            callCenter: callCenterFormValues(editingCountry.callCenter),
          }}
          validationSchema={formValidation}
          onSubmit={handleSubmit}>
          {({ isValid, values }) => (
            <>
              <Row>
                <Col md="3">
                  <Input type="text" name="name" id="name" />
                </Col>
                <Col md="2">
                  <Input
                    type="text"
                    id="countryCode"
                    name="countryCode"
                    label="Country Code"
                  />
                  <p className="font-size-12 text-muted">
                    Ex. US, UK, etc. (ISO 3166-1 alpha-2)
                  </p>
                </Col>
                <Col md="3">
                  <Input
                    id="waitingTimeRecall"
                    name="waitingTimeRecall"
                    label="Waiting Time Recall"
                    min={0.5}
                    type="number"
                    step="0.1"
                  />
                  <p className="font-size-12 text-muted">
                    In hours (ex. 0,5 or 2)
                  </p>
                </Col>
                <Col md="2">
                  <Switch
                    name="active"
                    label="Active?"
                    defaultChecked={active}
                    onClick={() => setActive(!active)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <Select
                    name="phoneExchange"
                    id="phoneExchange"
                    label="Phone Exchange"
                    isMulti={false}
                    options={companyPhoneExchanges}
                  />
                </Col>
                <Col md="3">
                  <Select
                    name="timezone"
                    id="timezone"
                    label="Timezone"
                    isMulti={false}
                    options={TIMEZONES}
                  />
                </Col>
                <Col md="3">
                  <Select
                    name="languageTag"
                    id="languageTag"
                    label="Language"
                    isMulti={false}
                    options={LANGUAGE_TAGS}
                    // The AI agent writes to the lead in this language: the
                    // engine refuses to route a lead of a country without one.
                    required={aiModule}
                  />
                  <p className="font-size-12 text-muted">
                    Used as default language for landing pages
                  </p>
                </Col>
              </Row>
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">{t('Call center opening hours')}</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              <p className="font-size-12 text-muted">
                {t(
                  'Read in the time zone of the country. A country with no open day has no call center: the AI agent books no appointment and sends no reminder.',
                )}
                {values.timezone && ` (${values.timezone})`}
              </p>
              {WEEKDAYS.map(([day, label]) => {
                const isOpen =
                  values.callCenter?.workingTime?.[day]?.open === true;
                return (
                  <Row key={day} className="align-items-start">
                    <Col md="2" className="pt-4">
                      {/*
                        No defaultChecked, unlike the module switches below:
                        those are driven by a local useState, these by the
                        Formik value. Passing both makes React call the input
                        controlled and uncontrolled at once; `checked` would
                        win and the result would be the same, but by accident.
                      */}
                      <Switch
                        name={`callCenter.workingTime.${day}.open`}
                        label={t(label)}
                      />
                    </Col>
                    <Col md="3">
                      {/*
                        readOnly and not disabled: the form-field Input sets
                        `disabled` from readOnly and would override it.
                      */}
                      <Input
                        type="time"
                        name={`callCenter.workingTime.${day}.start`}
                        label={t('Opens at')}
                        readOnly={!isOpen}
                      />
                    </Col>
                    <Col md="3">
                      <Input
                        type="time"
                        name={`callCenter.workingTime.${day}.end`}
                        label={t('Closes at')}
                        readOnly={!isOpen}
                        subText={
                          day === 1
                            ? t(
                                'An appointment can be booked up to one minute before this time, so write the hour by which a call must be able to finish, not the hour the call center turns off the lights.',
                              )
                            : undefined
                        }
                      />
                    </Col>
                  </Row>
                );
              })}
              <Row>
                <Col md="6">
                  <Input
                    type="textarea"
                    name="callCenter.recurringClosedDays"
                    label={t('Closed every year')}
                    rows="4"
                    placeholder={'01-01\n12-31'}
                    subText={t(
                      'One MM-DD per line, for the days that are closed every single year. Written once, they never expire.',
                    )}
                  />
                </Col>
                <Col md="6">
                  <Input
                    type="textarea"
                    name="callCenter.closedDays"
                    label={t('Closed on these dates')}
                    rows="4"
                    placeholder={'2027-03-29\n2027-05-17'}
                    subText={t(
                      'One YYYY-MM-DD per line, for the closures whose date changes from year to year.',
                    )}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="2">
                  <Switch
                    name="facebookModule"
                    label="Facebook Module"
                    defaultChecked={facebookModule}
                    onClick={() => setFacebookModule(!facebookModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="googleModule"
                    label="Google Module"
                    defaultChecked={googleModule}
                    onClick={() => setGoogleModule(!googleModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="supplierModule"
                    label="Supplier Module"
                    defaultChecked={supplierModule}
                    onClick={() => setSupplierModule(!supplierModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="audienceModule"
                    label="Audience Module"
                    defaultChecked={audienceModule}
                    onClick={() => setAudienceModule(!audienceModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="landingPageModule"
                    label="Landing Page Module"
                    defaultChecked={landingPageModule}
                    onClick={() => setLandingPageModule(!landingPageModule)}
                  />
                </Col>
                <Col md="2">
                  <Switch
                    name="aiModule"
                    label="AI Module"
                    defaultChecked={aiModule}
                    onClick={() => setAiModule(!aiModule)}
                  />
                </Col>
              </Row>
              <Collapse isOpen={facebookModule}>
                <Row>
                  <Col md="6">
                    <FieldArray
                      name="facebookAdAccountIds"
                      label="Facebook Ad Account Ids"
                      values={values}
                      defaultItem={{ accountId: '', datasetId: '' }}
                      fields={[
                        { name: 'accountId', label: 'Account ID', col: 5 },
                        { name: 'datasetId', label: 'Dataset ID', col: 5 },
                      ]}
                    />
                  </Col>
                  <Col md="6">
                    <Input
                      type="textarea"
                      name="facebookAccessToken"
                      id="facebookAccessToken"
                      label="Facebook Access Token"
                    />
                  </Col>
                </Row>
              </Collapse>
              <Collapse isOpen={aiModule}>
                <Row>
                  <Col md="6">
                    <Input
                      type="text"
                      name="zernioAccountId"
                      id="zernioAccountId"
                      label="Zernio Account ID"
                      // Conditional, and not a bare `required`: Collapse
                      // keeps its children MOUNTED and Bootstrap merely
                      // hides them, so an always-required field would sit in
                      // the form empty and invisible whenever the module is
                      // off. @availity/form sets no noValidate (it is Formik
                      // over reactstrap, which renders a bare form), so the
                      // browser would refuse to submit and could not even
                      // focus the field to say why: saving ANY country with
                      // the module off would silently do nothing.
                      required={aiModule}
                      subText={t(
                        'The account of the WhatsApp number this country writes from, one per country: the engine reads it when it routes a lead and refuses to start a conversation without it. The agent opening templates must be approved on this very number — a template approved for another number does not exist for this one.',
                      )}
                    />
                  </Col>
                </Row>
              </Collapse>
              <div className="modal-footer">
                <SubmitButton
                  disabled={!isValid || props.loading}
                  create={props.create}
                />
              </div>
            </>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

export default FormCountry;
